<!-- 分区计量-统计分析-漏损管理-漏损自动分析 -->
<template>
  <div class="a-leakage-analysis">
    <!-- 列表视图 -->
    <div v-if="currentView === 'list'">
      <!-- 搜索区域 -->
      <a-card :bordered="false" class="search-card">
        <div class="table-page-search-wrapper">
          <a-form layout="horizontal" :labelCol="{ span: 5 }" :wrapperCol="{ span: 16 }">
            <a-row :gutter="48">
              <a-col :md="6" :sm="12">
                <a-form-item label="片区选择">
                  <a-tree-select v-model="query.zoneId" style="width: 100%" :tree-data="zoneTreeData"
                    :replace-fields="{ title: 'zoneName', key: 'id', value: 'id', selectable: 'selectable' }"
                    :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }" placeholder="请选择片区" allow-clear
                    show-search treeNodeFilterProp="title" />
                </a-form-item>
              </a-col>
              <a-col :md="6" :sm="12">
                <a-form-item label="查询时间">
                  <a-range-picker v-model="query.dateRange" style="width: 100%" format="YYYY-MM-DD"
                    :placeholder="['开始时间', '结束时间']" />
                </a-form-item>
              </a-col>
              <a-col :md="24" :sm="24">
                <div style="margin-bottom: 20px; display: flex; justify-content: flex-start;">
                  <a-button type="primary" @click="handleQuery">查询</a-button>
                  <a-button style="margin-left: 12px" @click="handleReset">重置</a-button>
                  <a-button type="primary" style="margin-left: 12px" @click="handleExport">导出</a-button>
                  <a-button type="primary" style="margin-left: 12px" @click="handleSetting">漏损等级设置</a-button>
                </div>
              </a-col>
            </a-row>
          </a-form>
        </div>
      </a-card>

      <!-- 表格区域 -->
      <a-card :bordered="false" class="table-card">
        <!-- 未查询时显示提示框 -->
        <div v-if="!hasQueried" class="empty-state">
          <a-icon type="search" class="empty-icon" />
          <p class="empty-text">请选择片区和查询时间后点击查询按钮</p>
          <p class="empty-hint">选择片区 → 选择查询时间 → 点击"查询"按钮</p>
        </div>

        <!-- 已查询时显示表格 -->
        <div v-else>
          <a-table :columns="columns" :data-source="tableData" :pagination="false" :loading="loading"
            @change="handleTableChange" rowKey="id" :bordered="true">
            <!-- 漏损等级列自定义渲染 -->
            <span slot="leakageLevel" slot-scope="text, record">
              <a-tag :color="getLeakageLevelColor(text, record)" :style="getLeakageLevelStyle(text, record)">
                {{ text }}
              </a-tag>
            </span>

            <!-- 操作列自定义渲染 -->
            <span slot="action" slot-scope="text, record">
              <a @click="handleDetail(record)">分区漏损明细</a>
              <a-divider type="vertical" />
              <a @click="handleUserDetail(record)">平行大用户明细</a>
            </span>
          </a-table>
        </div>
      </a-card>
      <leakage-level-modal ref="leakageLevelModal" @refresh="refreshHandleQuery" />
    </div>

    <!-- 详情视图 -->
    <zone-leakage-detail v-else-if="currentView === 'detail'" :record="currentRecord"
      :initial-date-range="query.dateRange" @back="currentView = 'list'" />

    <!-- 平行大用户明细视图 -->
    <parallel-user-detail v-else-if="currentView === 'user'" :record="currentRecord"
      :initial-date-range="query.dateRange" @back="currentView = 'list'" />
  </div>
</template>

<script>
import moment from 'moment'
import axios from 'axios'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import storage from 'store'
import LeakageLevelModal from './modules/LeakageLevelModal'
import ZoneLeakageDetail from './modules/ZoneLeakageDetail'
import ParallelUserDetail from './modules/ParallelUserDetail'

const columns = [
  {
    title: '分区级别',
    dataIndex: 'areaLevel',
    align: 'center',
    width: 100
  },
  {
    title: '片区名称',
    dataIndex: 'areaName',
    align: 'center',
    width: 150
  },
  {
    title: '供水量',
    dataIndex: 'supplyWater',
    align: 'center',
    width: 120
  },
  {
    title: '监控表水量',
    dataIndex: 'monitorWater',
    align: 'center',
    width: 120
  },
  {
    title: '平行大用户水量',
    dataIndex: 'bigUserWater',
    align: 'center',
    width: 150
  },
  {
    title: '水量差',
    dataIndex: 'waterDifference',
    align: 'center',
    width: 120
  },
  {
    title: '产销差率%',
    dataIndex: 'productionMarketing',
    align: 'center',
    width: 120,
    sorter: (a, b) => {
      const valA = parseFloat(a.productionMarketing) || 0
      const valB = parseFloat(b.productionMarketing) || 0
      return valB - valA
    }
  },
  {
    title: '漏损等级',
    dataIndex: 'leakageLevel',
    align: 'center',
    width: 120,
    scopedSlots: { customRender: 'leakageLevel' }
  },
  {
    title: '操作',
    key: 'action',
    align: 'center',
    width: 250,
    scopedSlots: { customRender: 'action' }
  }
]


export default {
  name: 'ALeakageAnalysis',
  components: {
    LeakageLevelModal,
    ZoneLeakageDetail,
    ParallelUserDetail
  },
  data () {
    return {
      currentView: 'list', // 'list' | 'detail'
      currentRecord: {},
      query: {
        zoneId: undefined,
        dateRange: []
      },
      hasQueried: false, // 是否已经查询过
      zoneTreeData: [],
      loading: false,
      columns,
      tableData: [],
      queryUrl: '/zone-metering/leakage/level/rule/select/date'
    }
  },
  created () {
    this.getZoneData()
    // 移除初始查询，等待用户手动查询
    // this.handleQuery()
  },
  methods: {
    moment,
    // 获取片区数据
    getZoneData () {
      this.$http.get('zone-metering/zone-metering').then(({ data }) => {
        this.zoneTreeData = data
      })
    },
    // 刷新查询
    refreshHandleQuery () {
      if (this.hasQueried) {
        this.handleQuery()
      }
    },
    // 查询
    handleQuery () {
      // 校验
      if (!this.query.zoneId) {
        this.$message.warning('请先选择片区')
        return
      }
      if (!this.query.dateRange || this.query.dateRange.length !== 2) {
        this.$message.warning('请先选择查询时间')
        return
      }

      this.loading = true
      this.hasQueried = true // 标记为已查询

      const params = {
        zoneId: this.query.zoneId,
        bgeinDate: this.query.dateRange[0].format('YYYY-MM-DD'),
        endDate: this.query.dateRange[1].format('YYYY-MM-DD')
      }

      this.$http.get(this.queryUrl, { params }).then(({ data }) => {
        console.log('漏损分析数据:', data)
        // 假设返回结构为 { list: [], total: 0 } 或直接是数组
        if (data && data.list) {
          this.tableData = data.list
        } else if (Array.isArray(data)) {
          this.tableData = data
        } else {
          this.tableData = []
        }
      }).catch(err => {
        console.error('查询失败:', err)
        this.$message.error('查询失败')
      }).finally(() => {
        this.loading = false
      })
    },
    // 重置
    handleReset () {
      this.hasQueried = false
      this.query = {
        zoneId: undefined,
        dateRange: []
      }
    },
    // 导出
    handleExport () {
      if (!this.query.zoneId) {
        this.$message.warning('请先选择片区')
        return
      }
      if (!this.query.dateRange || this.query.dateRange.length !== 2) {
        this.$message.warning('请先选择查询时间')
        return
      }

      this.loading = true
      const token = storage.get(ACCESS_TOKEN)
      const params = {
        zoneId: this.query.zoneId,
        bgeinDate: this.query.dateRange[0].format('YYYY-MM-DD'),
        endDate: this.query.dateRange[1].format('YYYY-MM-DD')
      }

      axios({
        url: process.env.VUE_APP_API_BASE_URL + 'zone-metering/leakage/level/rule/export',
        method: 'get',
        params: params,
        responseType: 'blob',
        headers: {
          Authorization: token
        }
      }).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' })
        const fileName = '漏损自动分析.xlsx'
        if ('download' in document.createElement('a')) { // 非IE下载
          const elink = document.createElement('a')
          elink.download = fileName
          elink.style.display = 'none'
          elink.href = URL.createObjectURL(blob)
          document.body.appendChild(elink)
          elink.click()
          URL.revokeObjectURL(elink.href) // 释放URL 对象
          document.body.removeChild(elink)
        } else { // IE10+下载
          navigator.msSaveBlob(blob, fileName)
        }
        this.$message.success('导出成功')
      }).catch(err => {
        console.error('导出失败:', err)
        this.$message.error('导出失败')
      }).finally(() => {
        this.loading = false
      })
    },
    // 漏损等级设置
    handleSetting () {
      this.$refs.leakageLevelModal.init()
    },
    // 表格分页/排序变化
    handleTableChange (pagination, filters, sorter) {
      // 这里可以处理排序逻辑
      this.handleQuery()
    },
    // 详情跳转
    handleDetail (record) {
      console.log('查看详情', record)
      this.currentRecord = record
      this.currentView = 'detail'
    },
    // 用户明细跳转
    handleUserDetail (record) {
      console.log('查看用户明细', record)
      this.currentRecord = record
      this.currentView = 'user'
    },
    // 获取漏损等级颜色
    getLeakageLevelColor (level, record) {
      if (record && record.leakageLevelColor) {
        return record.leakageLevelColor === '1' ? '#FFFFFF' : record.leakageLevelColor
      }
      const map = {
        '十分健康': '#00b42a', // 绿色
        '健康': '#00b42a',     // 绿色
        '观察': '#ff7d00',     // 橙色
        '严重': '#f53f3f',     // 红色
        '十分严重': '#780600'  // 深红/暗红
      }
      return map[level] || 'default'
    },
    getLeakageLevelStyle (level, record) {
      const color = this.getLeakageLevelColor(level, record)
      const isWhite = color === '#FFFFFF' || color === '#fff'
      return {
        width: '80px',
        textAlign: 'center',
        color: isWhite ? 'rgba(0, 0, 0, 0.65)' : '#fff',
        border: isWhite ? '1px solid #d9d9d9' : 'none'
      }
    }
  }
}
</script>

<style lang="less" scoped>
.a-leakage-analysis {
  .search-card {
    margin-bottom: 16px;
  }

  .table-card {
    background: #fff;
  }

  // 空状态提示样式
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    min-height: 400px;

    .empty-icon {
      font-size: 80px;
      color: #d9d9d9;
      margin-bottom: 20px;
    }

    .empty-text {
      font-size: 18px;
      color: #595959;
      margin-bottom: 8px;
      font-weight: 500;
    }

    .empty-hint {
      font-size: 14px;
      color: #8c8c8c;
      margin: 0;
    }
  }

  /deep/ .ant-tag {
    border: none;
    color: #fff;
    font-weight: bold;
  }
}
</style>